import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** The header's search field. Enter opens the results page; "/" focuses it. */
export function SearchBox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const current = useRouterState({
    select: (s) =>
      s.location.pathname === "/search"
        ? String((s.location.search as { q?: unknown }).q ?? "")
        : "",
  });
  const [value, setValue] = useState(current);
  const input = useRef<HTMLInputElement>(null);

  // Follow the URL (back/forward, or a search started elsewhere).
  useEffect(() => setValue(current), [current]);

  useEffect(() => {
    const focus = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      )
        return;
      e.preventDefault();
      input.current?.focus();
    };
    window.addEventListener("keydown", focus);
    return () => window.removeEventListener("keydown", focus);
  }, []);

  return (
    <form
      role="search"
      className="relative w-full max-w-md"
      onSubmit={(e) => {
        e.preventDefault();
        const q = value.trim();
        if (q) void navigate({ to: "/search", search: { q } });
      }}
    >
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        ref={input}
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label={t("search.label")}
        placeholder={t("search.placeholder")}
        maxLength={200}
        className="h-9 w-full rounded-md border bg-background pl-8 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </form>
  );
}
